import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const PremiumThankYou = ({ 
  title, 
  subtitle, 
  bulletPoints, 
  primaryButtonText = "Return to Home",
  primaryButtonLink = "/",
  secondaryButtonText,
  secondaryButtonLink,
  accentColor = "#1E3A8A"
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4 py-16 relative overflow-hidden">
      {/* Animated background decorations */}
      <motion.div
        className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl opacity-30"
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-64 h-64 bg-green-200 rounded-full filter blur-3xl opacity-30"
        animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full text-center relative z-10"
      >
        {/* Success Icon with animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-xl shadow-green-500/30"
            >
              <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center"
            >
              <Sparkles className="w-4 h-4 text-amber-900" />
            </motion.div>
          </div>
        </motion.div>

        {/* Main Header */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
        >
          {title}
        </motion.h1>

        {/* Subheading */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl text-gray-600 mb-6 font-medium"
        >
          {subtitle}
        </motion.p>

        {/* Description */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-gray-500 mb-10 max-w-lg mx-auto"
        >
          Thank you for reaching out. Our team will review everything and get back to you shortly.
        </motion.p>

        {/* What Happens Next Card */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-10 text-left"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-[#1E3A8A]" />
            </span>
            What happens next?
          </h2>

          <div className="space-y-4">
            {bulletPoints.map((point, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-sm">{index + 1}</span>
                </div>
                <p className="text-gray-700 leading-relaxed pt-1">
                  {point}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link to={primaryButtonLink}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-blue-500/25 font-semibold min-w-[200px]">
                {primaryButtonText}
              </Button>
            </motion.div>
          </Link>

          {secondaryButtonText && secondaryButtonLink && (
            <Link to={secondaryButtonLink}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  variant="outline" 
                  className="border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5 px-8 py-6 text-lg rounded-full font-semibold min-w-[200px]"
                >
                  {secondaryButtonText}
                </Button>
              </motion.div>
            </Link>
          )}
        </motion.div>
        
        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-sm text-gray-400 mt-8"
        >
          Questions? Email us at <a href="mailto:hello@bm-assist.co" className="text-[#1E3A8A] hover:underline">hello@bm-assist.co</a>
        </motion.p>
      </motion.div>
    </div>
  );
};

export default PremiumThankYou;
